/**
 * Textos (`en`) de pantalla d'Existències del BackOffice per Lot + Lloc (OBR-143, OBR-146, OBR-14): ajust, «Lot nou d'ajust»,
 * bloqueig, estats i «A regularitzar». Namespaces `backoffice.stockLine` i `backoffice.stockRegularize`. Tall propi: no es toca
 * `ca.ts`. Les tres llengües han de tenir les mateixes claus (i18n.test.ts); cap text literal a les vistes.
 */

export const stockLineEn = {
  'backoffice.stockLine': {
    filterBlocked: 'Blocked',
    blockedSince: 'Blocked on {{date}}',
    blockedReason: 'Reason: {{reason}}',
    blockedNoReason: 'No written reason',
    blockedHint:
      'Lots blocked by hand, receipt exceptions awaiting a decision and returns awaiting dispatch: not counted as available for production.',

    actionAdjust: 'Adjust',
    actionBlock: 'Block',
    actionUnblock: 'Unblock',
    actionNewLot: 'New adjustment lot',

    adjustBody:
      'The adjustment is made on this lot in this place. Movements are never edited: a new movement is recorded with the written reason.',
    adjustFieldLot: 'Lot',
    adjustFieldPlace: 'Place',
    adjustFieldBalance: 'Stock now',
    adjustNoLot: 'No lot',
    adjustAfter: '{{quantity}} {{unit}} will remain.',
    adjustNegative:
      'The balance cannot go below 0: this lot in this place only holds {{quantity}} {{unit}}.',
    adjustNoLotHint:
      'This stock has no lot: it can only be taken out. To add stock, use “New adjustment lot”.',
    adjustLotRequired: 'Adding stock needs a lot. Use “New adjustment lot”.',
    adjustExpiry: 'Lot expiry',
    adjustExpiryHint: 'This lot has none and the article requires it: enter it to add stock.',
    adjustExpiryInvalid: 'Enter an expiry date.',

    newLotTitle: 'New adjustment lot',
    newLotBody:
      'Adds stock that has no lot: a new internal lot is created and an adjustment movement is recorded with the written reason.',
    newLotSupplierCode: 'Supplier lot code',
    newLotSupplierCodeHint: 'Optional: the one printed on the pack.',
    newLotExpiry: 'Expiry',
    newLotExpiryRequired: 'Required for this article.',
    newLotExpiryOptional: 'Optional for this article.',
    newLotExpiryInvalid: 'Enter the expiry date.',
    newLotPlace: 'Place',
    newLotPlacePlaceholder: 'Choose where it is',
    newLotQuantity: 'Quantity',
    newLotQuantityHint: 'Greater than zero, in {{unit}}.',
    newLotQuantityInvalid: 'It must be greater than zero.',
    newLotQuantityTooMuch: 'It cannot exceed the {{quantity}} {{unit}} held without a lot.',
    newLotReason: 'Reason',
    newLotConfirm: 'Create lot',
    newLotCreating: 'Creating…',
    newLotProduction:
      'This article is produced: its lot is generated by the work order when it is closed and cannot be created by hand. If the stock does not add up, adjust a lot that already exists.',
    newLotNoStock: 'This article has no stock control: there is no stock to add.',
    noDefaultPlace: 'No default place',
    noDefaultPlaceHint:
      'This article has no default place. Pick one or the stock will stay “No place” and will need regularising.',
    defaultPlaceHint: 'The article’s default place.',
    defaultPlaceUnavailableHint:
      'The article’s default place no longer takes stock. Pick another or the stock will stay “No place”.',
    // Warning when the chosen destination already has the lot blocked (it does not block the move).
    destinationBlocked_one: 'The destination has this lot blocked: whatever you move there will stay blocked.',
    destinationBlocked_other:
      'The destination has {{count}} of these lots blocked: whatever you move there will stay blocked.',

    blockTitle: 'Block the lot',
    unblockTitle: 'Unblock the lot',
    blockBody:
      'A blocked lot cannot be consumed or shipped and the plant app does not offer it. The stock is still physically there and you can unblock it whenever you want.',
    unblockBody: 'The lot will be available again to consume and ship.',
    stateReason: 'Reason',
    stateReasonPlaceholder: 'Optional: why it is blocked or unblocked',
    blockConfirm: 'Block',
    unblockConfirm: 'Unblock',
    stateWorking: 'Saving…',

    errNegative:
      'The balance cannot go below 0. The stock of this lot has changed: open it again to see what is left.',
    errLotRequired: 'Adding stock needs a lot. Use “New adjustment lot”.',
    errExpiryRequired: 'This article requires an expiry date: enter it.',
    errForbidden: 'You do not have permission to adjust stock.',
    errBadPlace: 'This place no longer takes stock. Pick another.',
    errWorkOrder:
      'This article is produced: its lot is created by a work order when it is closed and cannot be added by hand.',
    errStateForbidden:
      'Only administration, production managers and quality can block or unblock.',
    errGeneric: 'Could not save. Please try again.',
  },

  'backoffice.stockRegularize': {
    tab: 'To regularise',
    tabsLabel: 'Stock view',
    tabStock: 'Stock',
    title: 'Stock to regularise',
    subtitle:
      'Stock that cannot be traced or does not add up: no lot, negative, no expiry or no place.',
    filterKind: 'Problem',
    kindAll: 'All',
    kind_no_lot: 'No lot',
    kind_negative: 'Negative balance',
    kind_no_expiry: 'No expiry',
    kind_no_place: 'No place',
    hint_no_lot: 'It belongs to no lot: it cannot be traced or shipped.',
    hint_negative: 'More was used than was recorded. Adjust the balance with a written reason.',
    hint_no_expiry: 'The lot has no expiry date and the article requires one.',
    hint_no_place: 'It has no place: nobody knows where it is. Put it where it belongs.',
    colArticle: 'Article',
    colLot: 'Lot',
    colPlace: 'Place',
    colQuantity: 'Quantity',
    colProblem: 'Problem',
    empty: 'All in order: there is no stock to regularise.',
    emptyFiltered: 'No line matches the filter.',
    loadError: 'Could not load the list.',
    actionAssignLot: 'Assign a lot',
    actionAdjustZero: 'Adjust to zero',
    actionSetExpiry: 'Set the expiry',
    actionPlace: 'Put it in a place',
    noActionHint: 'This line is regularised from the plant app or with an adjustment lot.',
    assignTitle: 'Assign a new lot',
    assignBody:
      'An internal lot is created with the expiry you enter and this stock is moved into it; the line without a lot is left at zero.',
    assignPartial:
      'The lot was created, but the line without a lot could not be set to zero. Adjust it to zero from this list.',
    zeroTitle: 'Adjust to zero',
    zeroNote: 'Regularisation: line adjusted to zero.',
    assignNote: 'Regularisation: stock without a lot moved into a new lot.',
    expiryTitle: 'Set the lot expiry',
    expiryBody:
      'Only the lot’s expiry date changes; the stock and the movements are not touched.',
    expiryLabel: 'Expiry',
    expiryConfirm: 'Save the expiry',
    expiryInvalid: 'Enter the expiry date.',
    expiryForbidden: 'You do not have permission to change this lot’s expiry.',
    expiryError: 'Could not save the expiry.',
    expiryWorking: 'Saving…',
  },
} as const;
